/* @HEADER
 * @version 1.1.972 | 2026-10-26
 * @purpose The web app's root: the three-way choice between an unauthenticated screen, the landing and the authenticated app, and the authenticated app itself as ASSEMBLY — its state from one hook, its providers from one component, its screen from the route table, its project-switch lifecycle from the host. Every decision it used to make has a file of its own (App.tsx by meaning, ZVX-DEV-6131…6191); the history of what left is in the sidecar.
 * @sidecar App.tsx.header.md
 * @layer app | @hex _none_ | @ctx app
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7685, ZVX-DEV-6083, ZVX-DEV-6064, ZVX-DEV-144, ZVX-DEV-145, ZVX-DEV-148, ZVX-DEV-1103, ZVX-DEV-1106, ZVX-DEV-1154, ZVX-DEV-1248, ZVX-ANALYTICS-W2-INLINE, ZVX-ANALYTICS-W2-03, ZVX-DEV-1312, ZVX-DEV-1473, ZVX-KANVAS-EXTRACT-005, ZVX-DEV-1508, ZVX-DEV-1694, ZVX-DEV-2024, ZVX-DEV-2166-SWITCHER, ZVX-DEV-2261, ZVX-DEV-2369, ZVX-DEV-2376, ZVX-DEV-3257, ZVX-DEV-3259, ZVX-DEV-3416, ZVX-DEV-3897, ZVX-DEV-3899, ZVX-DEV-4420, ZVX-DEV-5563, ZVX-DEV-5933, ZVX-DEV-5935, ZVX-DEV-5944, ZVX-DEV-7775, ZVX-DEV-8099, ZVX-DEV-8278
import { AppShellWithLayoutDebug } from './app/AppShellWithLayoutDebug';
import { AuthenticatedAppProviders } from './app/AuthenticatedAppProviders';
import { AuthenticatedAppHost } from './app/AuthenticatedAppHost';
import { AuthedRouteScreen, type AuthedScreenHost } from './app/AuthedRouteScreen';
import { SpaceDeleteDialogHost } from './app/SpaceDeleteDialogHost';
import { NavLinkNotice } from './app/NavLinkNotice';
import { SpaceViewSwitch } from './features/kanban/SpaceViewSwitch';
import { readWorkspaceSettingsContext } from './features/auth/activeWorkspaceMembership';
import { useAuthenticatedAppState } from './hooks/useAuthenticatedAppState';
import { useUnauthRouteState, isVisitorAuthenticated } from './hooks/useUnauthRouteState';
import { shouldShowLanding } from './hooks/unauthRouteResolve';
import { UnauthScreen } from './features/auth/UnauthScreen';
// @ts-expect-error — framework-free .mjs decisions, typed by their JSDoc
import { isUnauthScreenRoute } from './features/auth/unauthRouteDecisions.mjs';
import { LandingPage, hasOfflineLicense } from './features/landing/public-api';
import { MessagesProvider } from './shared/i18n/MessagesContext';
import './features/kanban/board-status-bar.css'

// ZVX-DEV-2376: SaaS gate — the same constant useOnlineMode.ts reads; local/offline builds see no landing.
const ONLINE_MODE = import.meta.env.VITE_ONLINE_MODE === 'true'

interface AuthenticatedAppProps {
  /** ZVX-DEV-2745: called once after this mount's first project-data load, so the host can clear the switch overlay. */
  onReady?: () => void
}

/**
 * The authenticated app: state from `useAuthenticatedAppState`, providers from
 * `AuthenticatedAppProviders`, the screen from the route table. Extracted from
 * `App()` so the hook count stays constant whatever the unauth route is.
 */
function AuthenticatedApp({ onReady }: AuthenticatedAppProps = {}) {
  const s = useAuthenticatedAppState(onReady);
  const { nav } = s;

  const screenHost: AuthedScreenHost = {
    canGoBack: nav.canGoBack,
    onGoBack: nav.handleGoBack,
    onNavigate: nav.handleNavigate,
    spaceView: (
      <SpaceViewSwitch
        activeSpaceId={nav.activeSpaceId}
        activeView={nav.activeView}
        boardGroups={s.boardGroups}
        canGoBack={nav.canGoBack}
        boardMode={s.subscriptions.appBoardMode}
        overdueHaloEnabled={s.subscriptions.appOverdueHaloEnabled}
        onNavigate={nav.handleNavigate}
        onSelectSpace={nav.handleSelectSpace}
        onSwitchView={nav.handleSwitchView}
        onGoBack={nav.handleGoBack}
        onUpdateBoardGroup={s.handleUpdateBoardGroup}
        searchHighlightOnOpen={nav.searchHighlightOnOpen}
        onSearchHighlightOnOpenTaken={nav.onSearchHighlightOnOpenTaken}
        openCardOnArrival={nav.openCardOnArrival}
        onOpenCardOnArrivalTaken={nav.onOpenCardOnArrivalTaken}
      />
    ),
    readWorkspaceSettingsContext,
    // After delete, jump back to the boards landing.
    onWorkspaceDeleted: () => { window.location.hash = ''; },
  };

  return (
    <AuthenticatedAppProviders
      adapter={s.authAdapter}
      navigation={{ canGoBack: nav.canGoBack, onGoBack: nav.handleGoBack }}
      recordingRoute={`${s.route}${nav.activeSpaceId ? `:${nav.activeView}` : ''}`}
      dialogs={
        <>
          {/* ZVX-DEV-5637 — the question that used to not be asked. Portals to <body>. */}
          <SpaceDeleteDialogHost
            open={s.spaceDelete.pending !== null}
            spaceName={s.spaceDelete.pending?.spaceName ?? ''}
            boardCount={s.spaceDelete.pending?.boardCount ?? 0}
            destinations={s.spaceDelete.pending?.destinations ?? []}
            onConfirm={s.spaceDelete.confirm}
            onCancel={s.spaceDelete.cancel}
          />
          {/* ZVX-DEV-8265 — a link that has not opened says why; never an empty screen. */}
          <NavLinkNotice notice={s.navLink.notice} onRetry={s.navLink.retry} onToProjects={s.navLink.toProjects} />
        </>
      }
    >
      <AppShellWithLayoutDebug
        hash={s.authedHash}
        currentRoute={s.route}
        hashOverlay={s.hashOverlay !== null}
        onNavigate={nav.handleNavigate}
        boardGroups={s.boardGroups}
        activeBoardGroupId={nav.activeSpaceId}
        onSelectBoardGroup={nav.handleSelectSpace}
        onCreateBoardGroup={s.handleCreateBoardGroup}
        onDeleteBoardGroup={s.spaceDelete.request}
        activeView={nav.activeView}
        onSwitchView={nav.activeSpaceId ? nav.handleSwitchView : undefined}
        canGoBack={nav.canGoBack}
        onGoBack={nav.handleGoBack}
        pendingScrollRef={nav.pendingScrollRef}
        boardMode={s.subscriptions.appBoardMode}
        onOpenAccount={() => { window.location.hash = '#account'; }}
      >
        <AuthedRouteScreen route={s.route} hash={s.authedHash} host={screenHost} />
      </AppShellWithLayoutDebug>
    </AuthenticatedAppProviders>
  );
}

/**
 * The root: an unauthenticated screen (ZVX-DEV-6056), the landing for an
 * anonymous visitor on ONLINE_MODE without an offline license (ZVX-DEV-2376,
 * ZVX-DEV-2412), or the authenticated app under its project-switch host
 * (ZVX-DEV-2745).
 */
export default function App() {
  const unauth = useUnauthRouteState();
  if (isUnauthScreenRoute(unauth.route)) {
    return <UnauthScreen unauth={unauth} />;
  }
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const showLanding = shouldShowLanding({
    pathname,
    onlineMode: ONLINE_MODE,
    authenticated: isVisitorAuthenticated(),
    hasLicense: hasOfflineLicense(),
  })
  if (showLanding) {
    return (
      <MessagesProvider>
        <LandingPage />
      </MessagesProvider>
    )
  }
  return <AuthenticatedAppHost app={AuthenticatedApp} />;
}
